import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Account, AccountNote, AccountSummary } from '../../types';
import { apiService } from '../../services/api';

export type SortField = 'name' | 'balance' | 'creditLimit' | 'availableCredit';
export type SortDirection = 'asc' | 'desc';
export type ViewMode = 'card' | 'list';

interface AccountsState {
  accounts: Account[];
  filteredAccounts: Account[];
  currentAccount: Account | null;
  accountNotes: AccountNote[];
  summary: AccountSummary | null;
  searchTerm: string;
  statusFilter: string | null;
  showClosed: boolean;
  sortField: SortField;
  sortDirection: SortDirection;
  viewMode: ViewMode;
  isLoading: boolean;
  error: string | null;
}

const initialState: AccountsState = {
  accounts: [],
  filteredAccounts: [],
  currentAccount: null,
  accountNotes: [],
  summary: null,
  searchTerm: '',
  statusFilter: null,
  showClosed: false,
  sortField: 'name',
  sortDirection: 'asc',
  viewMode: 'card',
  isLoading: false,
  error: null
};

// Async thunks
export const fetchAccounts = createAsyncThunk(
  'accounts/fetchAccounts',
  async (_, { rejectWithValue }) => {
    try {
      return await apiService.getAccounts();
    } catch (error) {
      return rejectWithValue('Failed to fetch accounts');
    }
  }
);

export const fetchAccountById = createAsyncThunk(
  'accounts/fetchAccountById',
  async (contactID: string, { rejectWithValue }) => {
    try {
      const account = await apiService.getAccountById(contactID);
      if (!account) {
        return rejectWithValue('Account not found');
      }
      return account;
    } catch (error) {
      return rejectWithValue('Failed to fetch account');
    }
  }
);

export const fetchAccountNotes = createAsyncThunk(
  'accounts/fetchAccountNotes',
  async (accountID: string, { rejectWithValue }) => {
    try {
      return await apiService.getAccountNotes(accountID);
    } catch (error) {
      return rejectWithValue('Failed to fetch account notes');
    }
  }
);

export const addAccountNote = createAsyncThunk(
  'accounts/addAccountNote',
  async (note: Omit<AccountNote, 'id' | 'timestamp'>, { rejectWithValue }) => {
    try {
      return await apiService.addAccountNote(note);
    } catch (error) {
      return rejectWithValue('Failed to add account note');
    }
  }
);

export const fetchAccountSummary = createAsyncThunk(
  'accounts/fetchAccountSummary',
  async (_, { rejectWithValue }) => {
    try {
      return await apiService.getAccountSummary();
    } catch (error) {
      return rejectWithValue('Failed to fetch account summary');
    }
  }
);

export const createAccount = createAsyncThunk(
  'accounts/createAccount',
  async (account: Omit<Account, 'id'>, { rejectWithValue }) => {
    try {
      return await apiService.createAccount(account);
    } catch (error) {
      return rejectWithValue('Failed to create account');
    }
  }
);

export const updateAccount = createAsyncThunk(
  'accounts/updateAccount',
  async (account: Account, { rejectWithValue }) => {
    try {
      // Create a clean account object without the invoices navigation property
      const { invoices, ...accountData } = account;
      
      // Perform the update
      let updateSuccess = false;
      if (account.id) {
        const result = await apiService.updateAccountById(account.id, accountData);
        updateSuccess = result !== undefined;
      } else {
        const result = await apiService.updateAccount(account.contactID, accountData);
        updateSuccess = result !== undefined;
      }
      
      if (!updateSuccess) {
        return rejectWithValue('Failed to update account');
      }
      
      // Fetch the updated account data since the API doesn't return it
      const updatedAccount = await apiService.getAccountById(account.contactID);
      if (!updatedAccount) {
        return rejectWithValue('Failed to fetch updated account');
      }
      
      return updatedAccount;
    } catch (error) {
      return rejectWithValue('Failed to update account');
    }
  }
);

// Accounts slice
const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.filteredAccounts = filterAccounts(state.accounts, action.payload, state.statusFilter, state.showClosed);
    },
    setStatusFilter: (state, action: PayloadAction<string | null>) => {
      state.statusFilter = action.payload;
      state.filteredAccounts = filterAccounts(state.accounts, state.searchTerm, action.payload, state.showClosed);
    },
    setShowClosed: (state, action: PayloadAction<boolean>) => {
      state.showClosed = action.payload;
      state.filteredAccounts = filterAndSortAccounts(state.accounts, state.searchTerm, state.statusFilter, action.payload, state.sortField, state.sortDirection);
    },
    setSortField: (state, action: PayloadAction<SortField>) => {
      state.sortField = action.payload;
      state.filteredAccounts = filterAndSortAccounts(state.accounts, state.searchTerm, state.statusFilter, state.showClosed, action.payload, state.sortDirection);
    },
    setSortDirection: (state, action: PayloadAction<SortDirection>) => {
      state.sortDirection = action.payload;
      state.filteredAccounts = filterAndSortAccounts(state.accounts, state.searchTerm, state.statusFilter, state.showClosed, state.sortField, action.payload);
    },
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },
    clearFilters: (state) => {
      state.searchTerm = '';
      state.statusFilter = null;
      state.filteredAccounts = filterAndSortAccounts(state.accounts, '', null, state.showClosed, state.sortField, state.sortDirection);
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch accounts
      .addCase(fetchAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action: PayloadAction<Account[]>) => {
        state.isLoading = false;
        state.accounts = action.payload;
        state.filteredAccounts = filterAccounts(action.payload, state.searchTerm, state.statusFilter, state.showClosed);
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to fetch accounts';
      })
      
      // Fetch account by ID
      .addCase(fetchAccountById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAccountById.fulfilled, (state, action: PayloadAction<Account>) => {
        state.isLoading = false;
        state.currentAccount = action.payload;
      })
      .addCase(fetchAccountById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to fetch account';
      })
      
      // Fetch account notes
      .addCase(fetchAccountNotes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAccountNotes.fulfilled, (state, action: PayloadAction<AccountNote[]>) => {
        state.isLoading = false;
        state.accountNotes = action.payload;
      })
      .addCase(fetchAccountNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to fetch account notes';
      })
      
      // Add account note
      .addCase(addAccountNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addAccountNote.fulfilled, (state, action: PayloadAction<AccountNote>) => {
        state.isLoading = false;
        state.accountNotes.push(action.payload);
      })
      .addCase(addAccountNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to add account note';
      })
      
      // Fetch account summary
      .addCase(fetchAccountSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAccountSummary.fulfilled, (state, action: PayloadAction<AccountSummary>) => {
        state.isLoading = false;
        state.summary = action.payload;
      })
      .addCase(fetchAccountSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to fetch account summary';
      })
      
      // Create account
      .addCase(createAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAccount.fulfilled, (state, action: PayloadAction<Account>) => {
        state.isLoading = false;
        state.accounts.push(action.payload);
        state.filteredAccounts = filterAccounts(
          state.accounts,
          state.searchTerm,
          state.statusFilter,
          state.showClosed
        );
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to create account';
      })
      
      // Update account
      .addCase(updateAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAccount.fulfilled, (state, action: PayloadAction<Account>) => {
        state.isLoading = false;
        // Update the account in the accounts array
        const index = state.accounts.findIndex(acc => acc.contactID === action.payload.contactID);
        if (index !== -1) {
          state.accounts[index] = action.payload;
        }
        // Update the current account if it's the one being edited
        if (state.currentAccount && state.currentAccount.contactID === action.payload.contactID) {
          state.currentAccount = action.payload;
        }
        // Update filtered accounts
        state.filteredAccounts = filterAccounts(
          state.accounts,
          state.searchTerm,
          state.statusFilter,
          state.showClosed
        );
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to update account';
      });
  }
});

// Helper function to filter and sort accounts
const filterAndSortAccounts = (
  accounts: Account[],
  searchTerm: string,
  statusFilter: string | null,
  showClosed: boolean,
  sortField: SortField,
  sortDirection: SortDirection
): Account[] => {
  let filtered = [...accounts];
  
  // Apply search term filter
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(account => 
      account.name.toLowerCase().includes(term) ||
      account.contactID.toLowerCase().includes(term) ||
      account.hsID.toLowerCase().includes(term)
    );
  }
  
  // Apply status filter
  if (statusFilter) {
    filtered = filtered.filter(account => account.accountStatus === statusFilter);
  }
  
  // Hide closed accounts if showClosed is false
  if (!showClosed) {
    filtered = filtered.filter(account => account.accountStatus !== 'Closed');
  }
  
  // Apply sorting
  filtered.sort((a, b) => {
    let aValue: any;
    let bValue: any;
    
    switch (sortField) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'balance':
        aValue = a.balance;
        bValue = b.balance;
        break;
      case 'creditLimit':
        aValue = a.creditLimit;
        bValue = b.creditLimit;
        break;
      case 'availableCredit':
        aValue = a.availableCredit;
        bValue = b.availableCredit;
        break;
      default:
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
    }
    
    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });
  
  return filtered;
};

// Helper function to filter accounts (for backward compatibility)
const filterAccounts = (
  accounts: Account[],
  searchTerm: string,
  statusFilter: string | null,
  showClosed: boolean
): Account[] => {
  return filterAndSortAccounts(accounts, searchTerm, statusFilter, showClosed, 'name', 'asc');
};

export const { setSearchTerm, setStatusFilter, setShowClosed, setSortField, setSortDirection, setViewMode, clearFilters } = accountsSlice.actions;
export default accountsSlice.reducer;
